import { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import { ExclamationIcon, PlusIcon } from '@heroicons/react/outline'
import { ButtonLoader, Notification } from '../../../components'
import { Loader } from '../../../components/Loader'
import { FormattedRelativeTime } from '../../../components/FormattedRelativeTime'
import { fetchCoverageEvents } from '../../../services/coverageEventServices'
import { coverageEventQueryKeys } from './coverageEventQueryKeys'
import { eventLabel } from './eventLabel'
import { CreateCoverageEventDialog } from './CreateCoverageEventDialog'
import { CoverageEventDetailPanel } from './CoverageEventDetailPanel'
import { TablePaginator } from './TablePaginator'
import { Pill } from './Pill'

const T = 'cycle_counts.events'
const STATUS_COLORS = {
  open: 'bg-blue-100 text-blue-800',
  reconciling: 'bg-yellow-100 text-yellow-800',
  pending_approval: 'bg-orange-100 text-orange-800',
  signed: 'bg-indigo-100 text-indigo-800',
  closed: 'bg-green-100 text-green-800',
  canceled: 'bg-gray-100 text-gray-800',
}
// `signed` is a derived reconciliation state, not a status: the row badges it from the header,
// and the platform folds it into the status filter (`status=signed`) so the select stays one control.
const listStatus = event => (event?.reconciliation_state === 'signed' ? 'signed' : event?.status)

// openEventId/onOpenEventHandled let a sibling screen (the Conteos row's "Evento #id"
// link) drive this tab into a specific event's detail without lifting all of its state.
export const CoverageEventsTab = ({ warehouseId, active, openEventId, onOpenEventHandled, onCountsChanged }) => {
  const { i18n } = useTranslation()
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [createOpen, setCreateOpen] = useState(false)
  const [detailId, setDetailId] = useState(openEventId ?? null)
  const [gateOff, setGateOff] = useState(false)
  const [notice, setNotice] = useState(false)

  useEffect(() => {
    if (openEventId == null) return
    setDetailId(openEventId)
    onOpenEventHandled?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openEventId])

  const params = useMemo(() => ({ warehouse_id: warehouseId, page, per_page: 25, ...(status ? { status } : {}) }), [warehouseId, page, status])
  const { data, isLoading, isError, error, refetch } = useQuery(coverageEventQueryKeys.list(warehouseId, params), () => fetchCoverageEvents(params), {
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    enabled: active,
    refetchOnMount: 'always',
  })

  const rows = data?.data ?? []
  const meta = data?.meta ?? { total_pages: 0, current_page: 1 }
  // An older platform 404s the whole endpoint; that is a distinct "not supported here" state,
  // never the ordinary empty list (which the endpoint answers with 200 and an empty array).
  const unavailable = isError && error?.response?.status === 404

  return (
    <div className="mt-4">
      {gateOff && (
        <div className="flex items-start gap-3 rounded-md bg-amber-50 border border-amber-200 p-4 mb-4 text-amber-800" role="status">
          <ExclamationIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
          <div>
            <p className="font-medium">{i18n.t(`${T}.gate.banner_title`)}</p>
            <p className="text-sm">{i18n.t(`${T}.gate.banner_body`)}</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <label className="text-sm text-gray-700">
          <span className="mr-2">{i18n.t(`${T}.columns.status`)}</span>
          <select
            aria-label={i18n.t(`${T}.columns.status`)}
            className="border-gray-300 rounded-md text-sm"
            value={status}
            onChange={e => {
              setStatus(e.target.value)
              setPage(1)
            }}
          >
            <option value="">{i18n.t(`${T}.status_filter.all`)}</option>
            <option value="open">{i18n.t(`${T}.status_filter.open`)}</option>
            <option value="reconciling">{i18n.t(`${T}.status_filter.reconciling`)}</option>
            <option value="pending_approval">{i18n.t(`${T}.status_filter.pending_approval`)}</option>
            <option value="signed">{i18n.t(`${T}.status_filter.signed`)}</option>
            <option value="closed">{i18n.t(`${T}.status_filter.closed`)}</option>
            <option value="canceled">{i18n.t(`${T}.status_filter.canceled`)}</option>
          </select>
        </label>
        {!gateOff && (
          <ButtonLoader type="primary" onClick={() => setCreateOpen(true)} disabled={unavailable}>
            <PlusIcon className="h-4 w-4 mr-1 inline" aria-hidden="true" />
            {i18n.t(`${T}.create_button`)}
          </ButtonLoader>
        )}
      </div>

      {isLoading ? (
        <Loader show />
      ) : unavailable ? (
        <p className="text-sm text-gray-500">{i18n.t(`${T}.unavailable`)}</p>
      ) : isError ? (
        <p className="text-sm text-red-700">{i18n.t(`${T}.load_error`)}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">{i18n.t(`${T}.empty`)}</p>
      ) : (
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['label', 'store', 'trigger', 'created_by', 'created_at', 'coverage', 'status'].map(col => (
                <th key={col} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  {i18n.t(`${T}.columns.${col}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.map(event => (
              <tr
                key={event.id}
                className="cursor-pointer hover:bg-gray-50"
                tabIndex={0}
                role="button"
                onClick={() => setDetailId(event.id)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setDetailId(event.id)
                  }
                }}
              >
                <td className="px-4 py-2 text-sm text-gray-900">{eventLabel(event, i18n)}</td>
                <td className="px-4 py-2 text-sm text-gray-500">{event.store_id ?? '-'}</td>
                <td className="px-4 py-2 text-sm text-gray-500">{i18n.t(`${T}.trigger.${event.trigger}`)}</td>
                <td className="px-4 py-2 text-sm text-gray-500">{event.created_by?.full_name}</td>
                <td className="px-4 py-2 text-sm text-gray-500">
                  <FormattedRelativeTime date={event.created_at} />
                </td>
                <td className="px-4 py-2 text-sm text-gray-900">{`${event.coverage?.satisfied ?? 0} / ${event.coverage?.total ?? 0}`}</td>
                <td className="px-4 py-2 text-sm">
                  <Pill className={STATUS_COLORS[listStatus(event)] || STATUS_COLORS.open}>{i18n.t(`${T}.status.${listStatus(event)}`)}</Pill>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <TablePaginator page={page} meta={meta} onPage={setPage} />

      <CreateCoverageEventDialog
        open={createOpen}
        warehouseId={warehouseId}
        onCancel={() => setCreateOpen(false)}
        onGateOff={() => {
          setGateOff(true)
          setCreateOpen(false)
        }}
        onSuccess={created => {
          setCreateOpen(false)
          refetch()
          // A created event spawns new unassigned batches, changing the Conteos/Ubicaciones/
          // Revisiones pools shown on the other tabs — same reasoning as onUnassignSuccess below.
          onCountsChanged?.()
          setDetailId(created?.event?.id ?? null)
          setNotice(true)
        }}
      />
      <Notification show={notice} setShow={setNotice} type="success" title={i18n.t(`${T}.create.created`)} />
      <CoverageEventDetailPanel
        eventId={detailId}
        warehouseId={warehouseId}
        onClose={() => setDetailId(null)}
        onChanged={() => {
          refetch()
          // Adding locations or canceling an event also spawns/removes unassigned batches.
          onCountsChanged?.()
        }}
        onGateOff={() => setGateOff(true)}
      />
    </div>
  )
}
